import {
  membershipStatusMap, membershipRoleMap, joinFlowMap, membershipSubjectVisibilityMap,
} from '../models/membership-client-enums';
import { enrichMember } from './member-helpers-client';
import { capFirstChar } from './string-helpers-client.js';

const getCollectionSettingsFromMembership = (membership) => {
  const subjectCollection = membership.guild?.length ? 'guilds' : 'projects';
  const subjectRef = membership.guild || membership.project;
  const subjectCollection_titleCase = capFirstChar(subjectCollection);
  const subjectCollection_titleCaseSingular = subjectCollection_titleCase.slice(0, -1);
  const subjectCollection_singular = subjectCollection.slice(0, -1);
  const subjectIcon = subjectCollection === 'guilds' ? 'hive' : 'business_center';
  
  const targetCollection = membership.member?.length ? 'members' : 'projects';
  const targetRef = membership.member || membership.project;
  const targetCollection_titleCase = capFirstChar(targetCollection);
  const targetCollection_titleCaseSingular = targetCollection_titleCase.slice(0, -1);
  const targetCollection_singular = targetCollection.slice(0, -1);
  
  const membershipCollection = `${subjectCollection_singular}${targetCollection_titleCase}`;

  // Determine the visibility field on the subject
  const profileVisibilityField = `${targetCollection_singular}ProfileVisibility`;

  return {
    membershipCollection,

    subjectCollection, subjectRef, subjectCollection_titleCase, subjectCollection_titleCaseSingular,
    targetCollection, targetRef, targetCollection_titleCase, targetCollection_titleCaseSingular,
    subjectIcon,

    profileVisibilityField,
  };
};

const detailViewPathTemplate = (membershipCollection, subject, target) => {
  const subjectKey = subject?.key;
  const targetKey = target?.key || target?.number;

  if (!subjectKey || !targetKey) return null;

  if (membershipCollection === 'guildMembers') return `/${subjectKey}/@${targetKey}`;
  if (membershipCollection === 'projectMembers') return `/projects/${subjectKey}/@${targetKey}`;
  if (membershipCollection === 'guildProjects') return `/${subjectKey}/projects/${targetKey}`;
  
  return null;
};

/**
 * Enriches the membership with additional properties for display purposes.
 * 
 * @param {Object} membership - Required. The membership object to enrich.
 * @param {Object} refPathMap - Optional, but recommended. An object containing maps of related 
 *   objects by their ref path strings. They will be enriched in place as they are used.
 *   NOTE: Be sure to include the subject in this map in order to get visibility settings.
 */
export const enrichMembership = (membership, refPathMap) => {
  if (membership.isEnriched) return membership;

  const hasMap = Object.keys(refPathMap || {}).length;

  const statusSettings = membershipStatusMap[membership.status] || {};
  const roleSettings = membershipRoleMap[membership.role] || {};
  const joinFlowSettings = joinFlowMap[membership.joinFlow] || {};
  
  const {
    membershipCollection,
    subjectCollection, subjectRef, subjectCollection_titleCase, subjectCollection_titleCaseSingular,
    subjectIcon,
    targetCollection, targetRef, targetCollection_titleCase, targetCollection_titleCaseSingular,
    profileVisibilityField,
  } = getCollectionSettingsFromMembership(membership);
  
  const subject = refPathMap?.[subjectRef];
  const target = refPathMap?.[targetRef];

  const detailViewPath = detailViewPathTemplate(membershipCollection, subject, target);
  
  // Get the visibility settings IF the subject is in the refPathMap
  const visibility = subject?.[profileVisibilityField] || 'private';
  const visibilitySettings = membershipSubjectVisibilityMap[visibility];

  // Format is either 'Guild Organizer/Point' or 'Active/Proposed/Inactive Supporter/Contributor'
  const statusRoleLabel = ['organizer', 'point'].includes(membership.role)
    ? `${subjectCollection_titleCaseSingular} ${roleSettings.label}`
    : `${statusSettings.label} ${roleSettings.label}`;
  
  let status_timestampLabel, status_timestamp;
  if (membership.status == 'proposed')
    [status_timestampLabel, status_timestamp] = ['Proposed', membership.createdAt];
  else if (membership.status == 'active')
    [status_timestampLabel, status_timestamp] = ['Joined', membership.joinedAt];
  else if (membership.status == 'inactive')
    [status_timestampLabel, status_timestamp] = membership.firstApprovedBy
      ? ['Departed', membership.departedAt]
      : ['Declined', membership.departedAt];
  else if (membership.status == 'deleted')
    [status_timestampLabel, status_timestamp] = ['Departed', membership.departedAt];

  Object.assign(membership, {
    isEnriched: true,

    detailViewPath,
    ref: `${membershipCollection}/${membership.id}`,
    membershipCollection,

    subjectCollection,
    subjectCollection_titleCase,
    subjectCollection_titleCaseSingular,
    subjectIcon,
    subjectRef,
    subject,

    targetCollection,
    targetCollection_titleCase,
    targetCollection_titleCaseSingular,
    targetRef,
    target,

    visibility,
    visibility_icon: visibilitySettings?.icon || 'help',
    visibility_iconLibrary: visibilitySettings?.iconLibrary || 'material',
    visibility_label: visibilitySettings?.label || 'Unknown',
    visibility_description: visibilitySettings?.description || '',

    statusRoleLabel,

    status_timestampLabel,
    status_timestamp,
    timestamp: status_timestamp,

    status_icon: statusSettings.icon || 'help',
    status_iconLibrary: statusSettings.iconLibrary || 'material',
    status_label: statusSettings.label || 'Unknown',
    status_description: statusSettings.description || 'Unknown',

    role_icon: roleSettings.icon || 'help',
    role_iconLibrary: roleSettings.iconLibrary || 'material',
    role_label: roleSettings.label || 'Unknown',
    role_index: roleSettings.index || 0,

    joinFlow_label: joinFlowSettings.label || 'Unknown',

    profileUpdateCount_text: !membership.profileUpdateCount
      ? 'No edits'
      : (membership.profileUpdateCount === 1 ? '1 edit' : `${membership.profileUpdateCount} edits`),

    member: hasMap ? enrichMember(refPathMap[membership.member]) : membership.member,
    firstProposedBy: hasMap
      ? enrichMember(refPathMap[membership.firstProposedBy])
      : membership.firstProposedBy,
  });

  return membership;
};

